import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * Receipts are archived in S3 under a key whose extension the backend derives from the uploaded
 * content type (`.pdf`, `.jpg`/`.png`/…, or `.bin` when the type was unrecognised), so the key
 * alone tells us how to render it. The legacy Gupshup `imageUrl` has no key — those are images.
 */
function isPdfReceipt(sourceName?: string): boolean {
  const path = sourceName?.split('?')[0].toLowerCase();
  return path?.endsWith('.pdf') === true;
}

function filenameFor(sourceName: string | undefined, fallback: string): string {
  const name = sourceName?.split('?')[0].split('/').pop();
  return name && name.includes('.') ? name : fallback;
}

interface ReceiptPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves the URL to render. Re-run on every open — presigned links are short-lived. */
  resolveUrl: () => Promise<string>;
  /** S3 key (or filename): only its extension is read, to pick the PDF vs image renderer. */
  sourceName?: string;
}

/**
 * Modal viewer for a transfer receipt. Images render inline, PDFs in an embedded frame, and
 * anything the browser refuses to display falls back to a download card — the toolbar's download
 * and open-in-new-tab actions stay available in every state (mobile Safari, for one, won't render
 * a PDF in a frame).
 */
export function ReceiptPreviewDialog({ open, onOpenChange, resolveUrl, sourceName }: ReceiptPreviewDialogProps) {
  const { t } = useTranslation('orders');
  const [url, setUrl] = useState<string | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [renderFailed, setRenderFailed] = useState(false);

  // The callers pass an inline arrow; keeping it in a ref keeps the effect keyed on `open` alone
  // so the receipt isn't re-fetched on every parent render.
  const resolveUrlRef = useRef(resolveUrl);
  resolveUrlRef.current = resolveUrl;

  const isPdf = isPdfReceipt(sourceName);
  const filename = filenameFor(sourceName, isPdf ? 'receipt.pdf' : 'receipt');

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setUrl(null);
    setFetchFailed(false);
    setRenderFailed(false);

    resolveUrlRef.current().then(
      (resolved) => {
        if (!cancelled) setUrl(resolved);
      },
      () => {
        if (!cancelled) setFetchFailed(true);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleDownload = useCallback(async () => {
    if (!url) return;
    try {
      // No credentials: the presigned URL carries its own signature and is a third-party origin.
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // The blob route needs bucket CORS, and `download` is ignored cross-origin — when neither
      // is available, hand the URL to the browser and let it save or display it.
      window.open(url, '_blank', 'noopener');
    }
  }, [url, filename]);

  const showDownloadCard = fetchFailed || renderFailed;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="flex-row items-center justify-between gap-2 border-b border-border px-4 py-3 pr-12">
          <DialogTitle className="truncate text-sm font-medium">{t('transfer.title')}</DialogTitle>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={handleDownload} disabled={!url}>
              <Download className="h-4 w-4" />
              {t('transfer.viewer.download')}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => url && window.open(url, '_blank', 'noopener')}
              disabled={!url}
              title={t('transfer.viewer.openInNewTab')}
            >
              <ExternalLink className="h-4 w-4" />
              <span className="sr-only">{t('transfer.viewer.openInNewTab')}</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/40 p-4">
          {showDownloadCard ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <FileWarning className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">
                {fetchFailed ? t('transfer.receiptError') : t('transfer.viewer.previewUnavailable')}
              </p>
              {url && (
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4" />
                  {t('transfer.viewer.download')}
                </Button>
              )}
            </div>
          ) : !url ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('transfer.viewer.loading')}
            </div>
          ) : isPdf ? (
            <iframe
              src={url}
              title={t('transfer.title')}
              className="h-full w-full rounded border border-border bg-background"
            />
          ) : (
            <img
              src={url}
              alt={t('transfer.title')}
              className="max-h-full max-w-full rounded object-contain"
              onError={() => setRenderFailed(true)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
